import { cn } from '@/utils/cn'

import { type ComponentProps, type ComponentPropsWithoutRef, type ReactNode, forwardRef } from 'react'
import { Link } from 'react-router'
import { ArrowIcon } from '../icons/arrow'

export const Breadcrumb = forwardRef<
  HTMLElement,
  ComponentPropsWithoutRef<'nav'> & {
    separator?: ReactNode
  }
>(({ className, ...props }, ref) => (
  <nav
    ref={ref}
    aria-label="breadcrumb"
    className={cn('relative z-[1] flex items-center border-b bg-surface px-4 py-2 md:px-5', className)}
    {...props}
  />
))
Breadcrumb.displayName = 'Breadcrumb'

export const BreadcrumbList = forwardRef<HTMLOListElement, ComponentPropsWithoutRef<'ol'>>(
  ({ className, ...props }, ref) => (
    <ol ref={ref} className={cn('flex items-center gap-1 text-xs', className)} {...props} />
  )
)
BreadcrumbList.displayName = 'BreadcrumbList'

export const BreadcrumbItem = forwardRef<HTMLLIElement, ComponentPropsWithoutRef<'li'>>((props, ref) => (
  <li ref={ref} {...props} />
))
BreadcrumbItem.displayName = 'BreadcrumbItem'

export const BreadcrumbLink = forwardRef<HTMLAnchorElement, ComponentPropsWithoutRef<typeof Link>>(
  ({ className, ...props }, ref) => {
    return (
      <Link
        ref={ref}
        className={cn('rounded-xl outline-none transition-colors', 'hocus:text-accent focus-visible:ring', className)}
        {...props}
      />
    )
  }
)
BreadcrumbLink.displayName = 'BreadcrumbLink'

export const BreadcrumbPage = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<'span'>>(
  ({ className, ...props }, ref) => (
    <span ref={ref} role="link" aria-disabled="true" aria-current="page" className={cn(className)} {...props} />
  )
)
BreadcrumbPage.displayName = 'BreadcrumbPage'

export const BreadcrumbSeparator = ({ children, className, ...props }: ComponentProps<'li'>) => (
  <li role="presentation" aria-hidden="true" className={cn(className)} {...props}>
    {children ?? <ArrowIcon direction="right" size={16} />}
  </li>
)
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator'
